import React, { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  FireIcon,
  EyeIcon,
  SpeakerWaveIcon,
  TruckIcon,
  HomeModernIcon,
  ShieldExclamationIcon,
  HandRaisedIcon,
  HeartIcon,
  InformationCircleIcon,
  BoltIcon,
  CameraIcon,
  MapPinIcon,
  BellAlertIcon,
  UserGroupIcon,
  WrenchIcon,
  FlagIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  BeakerIcon,
  BugAntIcon,
  ChartBarIcon,
  EnvelopeIcon,
  KeyIcon,
  LightBulbIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SparklesIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { Incident } from '../../services/supabase';
import { cameras } from '../../data/cameras';
import { calculateDistance, extractCoordinatesFromIncident } from '../../utils/geoUtils';
import './IncidentList.css';

interface IncidentListProps {
  incidents: Incident[];
  onFocusIncident: (incidentId: string) => void;
}

// Define a priority score mapping (A is highest priority)
const priorityScores: { [key: string]: number } = {
  'A': 5,
  'B': 4,
  'C': 3,
  'I': 2,
  'O': 1
};

// Get the default priority score for unknown priorities
const getDefaultPriorityScore = (priority: string | undefined): number => {
  if (!priority) return 0;
  return priorityScores[priority] || 0;
};

// Calculate the closest camera distance for an incident
const getClosestCameraDistance = (incident: Incident): number => {
  const coords = extractCoordinatesFromIncident(incident);
  if (!coords) return Infinity;

  let closestDistance = Infinity;

  for (const camera of cameras) {
    const cameraLat = parseFloat(camera.lat);
    const cameraLng = parseFloat(camera.lon);

    const distance = calculateDistance(
      coords.lat,
      coords.lng,
      cameraLat,
      cameraLng
    );

    if (distance < closestDistance) {
      closestDistance = distance;
    }
  }

  return closestDistance;
};

// Get the camera location information
const getClosestCameraInfo = (incident: Incident): { distance: number, camera: string } => {
  const coords = extractCoordinatesFromIncident(incident);
  if (!coords) return { distance: Infinity, camera: 'Unknown' };

  let closestDistance = Infinity;
  let closestCamera = 'Unknown';

  for (const camera of cameras) {
    const cameraLat = parseFloat(camera.lat);
    const cameraLng = parseFloat(camera.lon);

    const distance = calculateDistance(
      coords.lat,
      coords.lng,
      cameraLat,
      cameraLng
    );

    if (distance < closestDistance) {
      closestDistance = distance;
      closestCamera = camera.location;
    }
  }

  return { distance: closestDistance, camera: closestCamera };
};

const IncidentList: React.FC<IncidentListProps> = ({ incidents, onFocusIncident }) => {
  const [sortedIncidents, setSortedIncidents] = useState<Incident[]>([]);
  const [sortBy, setSortBy] = useState<'closest' | 'recent'>('closest');
  const [visibleCount, setVisibleCount] = useState<number>(50);
  const [categoryIconMap, setCategoryIconMap] = useState<Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');

  // Reset pagination when search query changes
  useEffect(() => {
    setVisibleCount(50);
  }, [searchQuery]);

  // Debounce the search query to reduce re-sorting frequency
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 200);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    // Process and sort incidents according to selected sort
    const processIncidents = () => {
      // Add distance and camera information to each incident
      const enhancedIncidents = incidents.map(incident => {
        const cameraInfo = getClosestCameraInfo(incident);
        const priorityScore = getDefaultPriorityScore(incident.priority);

        return {
          ...incident,
          _closestCameraDistance: cameraInfo.distance,
          _closestCamera: cameraInfo.camera,
          _priorityScore: priorityScore
        } as any;
      });

      // Apply text search filter if present
      const query = debouncedQuery.toLowerCase();
      const filteredIncidents = query
        ? enhancedIncidents.filter((i: any) => {
          const haystack = [
            i.incident_category,
            i.incident_description,
            i.intersection,
            i.priority,
            i._closestCamera
          ]
            .filter(Boolean)
            .map((v: any) => String(v).toLowerCase());
          return haystack.some((field: string) => field.includes(query));
        })
        : enhancedIncidents;

      // Sort by chosen strategy
      const sorted = filteredIncidents.sort((a: any, b: any) => {
        if (sortBy === 'recent') {
          // Prefer corrected display datetime, then report_datetime, then incident_datetime
          const aSource = a._display_datetime || a.report_datetime || a.incident_datetime;
          const bSource = b._display_datetime || b.report_datetime || b.incident_datetime;
          const aTime = new Date(aSource).getTime();
          const bTime = new Date(bSource).getTime();
          return bTime - aTime; // Most recent first
        }
        // Default: closest first
        return a._closestCameraDistance - b._closestCameraDistance;
      });

      setSortedIncidents(sorted);
    };

    processIncidents();
  }, [incidents, sortBy, debouncedQuery]);

  // Build a unique icon mapping per category (same category => same icon, different categories => avoid repeats where possible)
  useEffect(() => {
    const categoriesInOrder: string[] = [];
    for (const i of incidents) {
      const c = (i.incident_category || '').trim();
      if (c && !categoriesInOrder.includes(c)) categoriesInOrder.push(c);
    }

    const POOL = [
      ExclamationTriangleIcon,
      FireIcon,
      EyeIcon,
      SpeakerWaveIcon,
      TruckIcon,
      HomeModernIcon,
      ShieldExclamationIcon,
      HandRaisedIcon,
      HeartIcon,
      BoltIcon,
      CameraIcon,
      MapPinIcon,
      BellAlertIcon,
      UserGroupIcon,
      WrenchIcon,
      FlagIcon,
      GlobeAltIcon,
      AcademicCapIcon,
      BeakerIcon,
      BugAntIcon,
      ChartBarIcon,
      EnvelopeIcon,
      KeyIcon,
      LightBulbIcon,
      LockClosedIcon,
      MagnifyingGlassIcon,
      MegaphoneIcon,
      PhoneIcon,
      ShieldCheckIcon,
      SparklesIcon,
      StarIcon,
      InformationCircleIcon
    ];

    const KEYWORDS: Array<{ test: (s: string) => boolean; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = [
      { test: s => s.includes('FIRE'), icon: FireIcon },
      { test: s => s.includes('SUICIDE') || s.includes('PSYCH') || s.includes('MENTAL'), icon: HeartIcon },
      { test: s => s.includes('ROBBERY') || s.includes('THEFT') || s.includes('BURGLARY'), icon: HandRaisedIcon },
      { test: s => s.includes('ASSAULT') || s.includes('BATTERY') || s.includes('FIGHT'), icon: ShieldExclamationIcon },
      { test: s => s.includes('SUSPICIOUS'), icon: EyeIcon },
      { test: s => s.includes('TRAFFIC') || s.includes('VEH'), icon: TruckIcon },
      { test: s => s.includes('NOISE'), icon: SpeakerWaveIcon },
      { test: s => s.includes('VANDAL'), icon: WrenchIcon },
      { test: s => s.includes('WEAPON') || s.includes('GUN'), icon: ShieldCheckIcon },
      { test: s => s.includes('WELL BEING') || s.includes('CHECK'), icon: BellAlertIcon },
      { test: s => s.includes('PROWLER') || s.includes('TRESPASS'), icon: HomeModernIcon },
      { test: s => s.includes('INTOXICATED') || s.includes('DRUNK'), icon: FlagIcon }
    ];

    const used = new Set<React.ComponentType<any>>();
    const map: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {};

    // First pass: apply keyword mappings uniquely
    for (const cat of categoriesInOrder) {
      const U = cat.toUpperCase();
      const match = KEYWORDS.find(k => k.test(U));
      if (match && !used.has(match.icon)) {
        map[cat] = match.icon;
        used.add(match.icon);
      }
    }

    // Second pass: assign remaining categories from pool without repeats; reuse mapping if already set
    let poolIdx = 0;
    for (const cat of categoriesInOrder) {
      if (map[cat]) continue;
      // find next unused icon
      while (poolIdx < POOL.length && used.has(POOL[poolIdx])) poolIdx++;
      const chosen = POOL[poolIdx] || InformationCircleIcon;
      map[cat] = chosen;
      used.add(chosen);
      poolIdx++;
    }

    setCategoryIconMap(map);
  }, [incidents]);

  const formatLocation = (incident: Incident) => {
    const point = incident.intersection_point;
    if (!point) return '';

    // Extract coordinates based on the format
    let lat, lng;

    if ('coordinates' in point && Array.isArray(point.coordinates)) {
      // GeoJSON or simple coordinates array format
      lng = point.coordinates[0];
      lat = point.coordinates[1];
    } else if ('latitude' in point && 'longitude' in point) {
      // Direct lat/lng format
      lat = point.latitude;
      lng = point.longitude;
    } else {
      return '';
    }

    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  };

  // Format distance for display
  const formatDistance = (feet: number): string => {
    if (feet === Infinity) return 'Too far';
    if (feet >= 5280) {
      const miles = (feet / 5280).toFixed(1);
      return `${miles} miles`;
    }
    return `${Math.round(feet)} feet`;
  };

  // Simple time-ago formatting
  const formatTimeAgo = (iso: string | undefined): string => {
    if (!iso) return '';
    const now = Date.now();
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return '';
    const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  // Pick an icon that matches the incident category
  const getIncidentIcon = (categoryRaw: string | undefined) => {
    const category = (categoryRaw || '').toUpperCase();
    if (category.includes('FIRE')) return FireIcon;
    if (category.includes('SUICIDE')) return ExclamationTriangleIcon;
    if (category.includes('MENTAL') || category.includes('WELL BEING')) return HeartIcon;
    if (category.includes('FIGHT') || category.includes('ASSAULT') || category.includes('BATTERY')) return ShieldExclamationIcon;
    if (category.includes('TRAFFIC') || category.includes('VEH')) return TruckIcon;
    if (category.includes('BURGLARY') || category.includes('TRESPASS')) return HomeModernIcon;
    if (category.includes('SUSPICIOUS')) return EyeIcon;
    if (category.includes('NOISE')) return SpeakerWaveIcon;
    if (category.includes('ROBBERY') || category.includes('THEFT')) return HandRaisedIcon;
    return InformationCircleIcon;
  };

  // Determine badge class based on distance
  const getDistanceBadgeClass = (distance: number): string => {
    if (distance <= 1000) return 'distance-close';
    if (distance <= 2000) return 'distance-medium';
    return 'distance-far';
  };

  const totalCount = incidents.length;
  const showingAllMatches = debouncedQuery.length > 0;
  const displayedIncidents = showingAllMatches ? sortedIncidents : sortedIncidents.slice(0, visibleCount);

  return (
    <div className="incident-list">
      <div className="incident-list-toolbar">
        <h2>
          {showingAllMatches
            ? `Search Results (${sortedIncidents.length} of ${totalCount})`
            : `Incidents by Proximity (${totalCount} total)`}
        </h2>
        <div className="incident-toolbar-right">
          <div className="incident-search-wrap">
            <MagnifyingGlassIcon className="incident-search-icon" />
            <input
              type="text"
              className="incident-search"
              placeholder="Search by category, description, location, camera..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search incidents"
            />
            {searchQuery && (
              <button
                type="button"
                className="incident-search-clear"
                aria-label="Clear search"
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
          <div className="sort-toggle" role="tablist" aria-label="Sort incidents">
            <button
              type="button"
              role="tab"
              aria-selected={sortBy === 'recent'}
              className={`sort-option ${sortBy === 'recent' ? 'active' : ''}`}
              onClick={() => setSortBy('recent')}
            >
              Most Recent
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sortBy === 'closest'}
              className={`sort-option ${sortBy === 'closest' ? 'active' : ''}`}
              onClick={() => setSortBy('closest')}
            >
              Closest
            </button>
          </div>
        </div>
      </div>
      {displayedIncidents.map((incident: any) => (
        <div
          key={incident.id}
          className={`incident-item ${getDistanceBadgeClass(incident._closestCameraDistance)}`}
        >
          <div className="incident-header">
            {(() => {
              const Icon = categoryIconMap[incident.incident_category] || InformationCircleIcon;
              return (
                <div className={`incident-icon-wrap priority-${incident.priority}`}>
                  <Icon className="incident-icon-svg" />
                </div>
              );
            })()}
            <h3>{incident.incident_category}</h3>
            <div className="incident-badges">
              <span className={`priority-badge priority-${incident.priority}`}>Priority {incident.priority}</span>
              <span className={`distance-badge ${getDistanceBadgeClass(incident._closestCameraDistance)}`}>{formatDistance(incident._closestCameraDistance)}</span>
              <span className="distance-badge">{formatTimeAgo(incident._display_datetime || incident.report_datetime || incident.incident_datetime)}</span>
            </div>
          </div>
          <div className="incident-details">
            <p><strong>Time:</strong> {new Date(incident._display_datetime || incident.report_datetime || incident.incident_datetime).toLocaleString()}</p>
            <p><strong>Location:</strong> {incident.intersection}</p>
            <p><strong>Nearest Camera:</strong> {incident._closestCamera} ({formatDistance(incident._closestCameraDistance)})</p>
            <p><strong>Description:</strong> {incident.incident_description || 'No description available'}</p>
            <button
              className="map-link"
              onClick={() => onFocusIncident(incident.id)}
            >
              View on Map
            </button>
          </div>
        </div>
      ))}
      {!showingAllMatches && incidents.length > visibleCount && (
        <div className="more-incidents-note">
          Showing {Math.min(visibleCount, incidents.length)} of {incidents.length} incidents
          <div style={{ marginTop: '8px' }}>
            <button
              type="button"
              className="map-link"
              onClick={() => setVisibleCount(c => Math.min(c + 50, incidents.length))}
            >
              Load 50 more
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentList; 